/**
 * ReferencesPage — Reference Library (light theme).
 */

import { useState, useEffect } from 'react'
import {
  Plus, LayoutList, LayoutGrid, Pencil, Trash2,
  Loader2, Sparkles, X, ChevronUp, ChevronDown, BookOpen, AlertCircle, Search, ShieldCheck,
} from 'lucide-react'
import api from '../../lib/api'
import { buildApa } from '../../utils/apa'

const SURFACE = '#FFFFFF'
const BG      = '#F4F7FC'
const BORDER  = '#DDE4EF'
const TEXT    = '#0C1629'
const MUTED   = '#506280'
const FAINT   = '#8EA4BC'
const GOLD    = '#2563eb'
const GOLD_BG = 'rgba(37,99,235,0.06)'

// ─── Type chip ────────────────────────────────────────────────────────────────

const TYPE_CFG = {
  JOURNAL: { bg: 'rgba(37,99,235,0.08)', color: '#1d4ed8', border: 'rgba(37,99,235,0.25)' },
  BOOK:    { bg: 'rgba(22,163,74,0.08)',  color: '#15803D', border: 'rgba(22,163,74,0.25)' },
  REPORT:  { bg: 'rgba(37,99,235,0.08)',  color: '#1D4ED8', border: 'rgba(37,99,235,0.25)' },
  WEBSITE: { bg: 'rgba(80,98,128,0.08)',  color: '#506280', border: 'rgba(80,98,128,0.25)' },
  OTHER:   { bg: 'rgba(80,98,128,0.08)',  color: '#506280', border: 'rgba(80,98,128,0.25)' },
}

// ─── Verification badge ───────────────────────────────────────────────────────
// NOT_FOUND is the one that matters: a DOI was claimed and it does not resolve,
// which is what a fabricated citation looks like. NO_MATCH is merely
// inconclusive — most books and reports are simply not in CrossRef.

const VERIFY_CFG = {
  VERIFIED:   { label: 'Verified',   color: '#15803D', bg: 'rgba(22,163,74,0.10)',  border: 'rgba(22,163,74,0.30)' },
  NOT_FOUND:  { label: 'Not found',  color: '#B91C1C', bg: 'rgba(220,38,38,0.10)',  border: 'rgba(220,38,38,0.30)' },
  NO_MATCH:   { label: 'No match',   color: '#B45309', bg: 'rgba(217,119,6,0.10)',  border: 'rgba(217,119,6,0.30)' },
  UNVERIFIED: { label: 'Unchecked',  color: '#8EA4BC', bg: 'rgba(80,98,128,0.06)',  border: 'rgba(80,98,128,0.20)' },
}

function VerifyChip({ status, note }) {
  const c = VERIFY_CFG[status] || VERIFY_CFG.UNVERIFIED
  return (
    <span title={note || ''} className="font-mono px-1.5 py-0.5 rounded-md whitespace-nowrap"
      style={{ fontSize: 9, background: c.bg, color: c.color, border: `1px solid ${c.border}` }}>
      {c.label}
    </span>
  )
}

function TypeChip({ type }) {
  const c = TYPE_CFG[type] || TYPE_CFG.OTHER
  return (
    <span className="font-mono text-xs px-2 py-0.5 rounded-md"
      style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}` }}>
      {type}
    </span>
  )
}

// ─── Shared input styles ─────────────────────────────────────────────────────

const inputCls = "w-full px-3 py-2.5 rounded-lg font-sans text-sm placeholder:text-[#8EA4BC] outline-none transition-all"
const inputSty = { background: BG, border: `1px solid ${BORDER}`, color: TEXT }
const focusHandlers = {
  onFocus: e => { e.currentTarget.style.borderColor = 'rgba(37,99,235,0.45)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.10)' },
  onBlur:  e => { e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.boxShadow = 'none' },
}

// ─── Modal ────────────────────────────────────────────────────────────────────

function Modal({ onClose, width = 600, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(12,22,41,0.45)' }}
      onClick={onClose}>
      <div className="rounded-2xl max-h-[90vh] overflow-y-auto shadow-elevated"
        style={{ background: SURFACE, border: `1px solid ${BORDER}`, width: '100%', maxWidth: width }}
        onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  )
}

// ─── Manual form ──────────────────────────────────────────────────────────────

const EMPTY_REF = { authors: '', year: '', title: '', journal: '', volume: '', issue: '', pages: '', doi: '', url: '', type: 'JOURNAL' }

function ManualForm({ initial = EMPTY_REF, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial)
  const set = k => e => setForm(prev => ({ ...prev, [k]: e.target.value }))
  const apaPreview = form.authors && form.title ? buildApa(form) : ''
  const canSave    = form.authors.trim() && form.title.trim()

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="font-sans text-xs font-medium mb-1 block" style={{ color: MUTED }}>
            Authors <span style={{ color: GOLD }}>*</span>
          </label>
          <input className={inputCls} style={inputSty} {...focusHandlers}
            placeholder="Smith, J., & Jones, M." value={form.authors} onChange={set('authors')} />
        </div>
        <div className="col-span-2">
          <label className="font-sans text-xs font-medium mb-1 block" style={{ color: MUTED }}>
            Title <span style={{ color: GOLD }}>*</span>
          </label>
          <input className={inputCls} style={inputSty} {...focusHandlers}
            placeholder="Paper or book title" value={form.title} onChange={set('title')} />
        </div>
        <div>
          <label className="font-sans text-xs font-medium mb-1 block" style={{ color: MUTED }}>Year</label>
          <input className={inputCls} style={inputSty} {...focusHandlers}
            placeholder="2024" type="number" value={form.year} onChange={set('year')} />
        </div>
        <div>
          <label className="font-sans text-xs font-medium mb-1 block" style={{ color: MUTED }}>Type</label>
          <select className={inputCls} style={{ ...inputSty, cursor: 'pointer' }} value={form.type} onChange={set('type')}>
            {['JOURNAL','BOOK','REPORT','WEBSITE','OTHER'].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="font-sans text-xs font-medium mb-1 block" style={{ color: MUTED }}>Journal / Publisher</label>
          <input className={inputCls} style={inputSty} {...focusHandlers}
            placeholder="Journal of …" value={form.journal} onChange={set('journal')} />
        </div>
        <div>
          <label className="font-sans text-xs font-medium mb-1 block" style={{ color: MUTED }}>Volume</label>
          <input className={inputCls} style={inputSty} {...focusHandlers} placeholder="12" value={form.volume} onChange={set('volume')} />
        </div>
        <div>
          <label className="font-sans text-xs font-medium mb-1 block" style={{ color: MUTED }}>Issue</label>
          <input className={inputCls} style={inputSty} {...focusHandlers} placeholder="3" value={form.issue} onChange={set('issue')} />
        </div>
        <div>
          <label className="font-sans text-xs font-medium mb-1 block" style={{ color: MUTED }}>Pages</label>
          <input className={inputCls} style={inputSty} {...focusHandlers} placeholder="123–145" value={form.pages} onChange={set('pages')} />
        </div>
        <div>
          <label className="font-sans text-xs font-medium mb-1 block" style={{ color: MUTED }}>DOI</label>
          <input className={inputCls} style={inputSty} {...focusHandlers} placeholder="10.1234/example" value={form.doi} onChange={set('doi')} />
        </div>
        <div className="col-span-2">
          <label className="font-sans text-xs font-medium mb-1 block" style={{ color: MUTED }}>URL</label>
          <input className={inputCls} style={inputSty} {...focusHandlers} placeholder="https://…" value={form.url} onChange={set('url')} />
        </div>
      </div>

      {apaPreview && (
        <div className="rounded-xl px-4 py-3" style={{ background: BG, border: `1px solid ${BORDER}` }}>
          <p className="font-mono mb-1" style={{ fontSize: 10, color: FAINT, letterSpacing: '0.06em' }}>APA PREVIEW</p>
          <p className="font-sans text-xs leading-relaxed italic" style={{ color: MUTED }}>{apaPreview}</p>
        </div>
      )}

      <div className="flex gap-3 pt-1">
        <button onClick={onCancel} className="flex-1 py-2.5 rounded-xl font-sans text-sm font-medium transition-all"
          style={{ border: `1px solid ${BORDER}`, color: MUTED }}
          onMouseEnter={e => e.currentTarget.style.background = BG}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          Cancel
        </button>
        <button onClick={() => onSave(form)} disabled={!canSave || saving}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-sans text-sm font-medium transition-all disabled:opacity-50"
          style={{ background: GOLD, color: '#FFFFFF' }}
          onMouseEnter={e => { if (!saving && canSave) e.currentTarget.style.background = '#3b82f6' }}
          onMouseLeave={e => e.currentTarget.style.background = GOLD}>
          {saving && <Loader2 style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} />}
          Save Reference
        </button>
      </div>
    </div>
  )
}

// ─── Paste & Parse modal ──────────────────────────────────────────────────────

function PasteParseModal({ onImport, onClose }) {
  const [raw,       setRaw]       = useState('')
  const [parsing,   setParsing]   = useState(false)
  const [parsed,    setParsed]    = useState(null)
  const [error,     setError]     = useState('')
  const [importing, setImporting] = useState(false)

  // Parsing runs on the backend: it has truncation-salvage JSON repair and
  // token governance, and it keeps the Anthropic key off the client.
  const parse = async () => {
    if (!raw.trim()) return
    setParsing(true); setError(''); setParsed(null)
    try {
      const { data } = await api.post('/references/parse', { rawText: raw, save: false })
      const list = data?.references || []
      if (!list.length) throw new Error('No references could be read from that text.')
      setParsed(list)
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Parse failed. Try again.')
    } finally { setParsing(false) }
  }

  const importAll = async () => {
    if (!parsed?.length) return
    setImporting(true)
    setError('')
    try {
      const { data } = await api.post('/references', { references: parsed })
      if (data?.skipped) {
        setError(`${data.skipped} already in your library — skipped. ${data.saved || 0} imported.`)
        if (!data.saved) return
      }
      onImport(data?.references || [])
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Import failed.')
    } finally { setImporting(false) }
  }

  return (
    <Modal onClose={onClose}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles style={{ width: 15, height: 15, color: GOLD }} />
            <p className="font-sans font-semibold text-sm" style={{ color: TEXT }}>Paste &amp; Parse with AI</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg transition-colors"
            onMouseEnter={e => e.currentTarget.style.background = BG}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <X style={{ width: 15, height: 15, color: FAINT }} />
          </button>
        </div>

        {!parsed ? (
          <>
            <p className="font-sans text-xs mb-3" style={{ color: FAINT }}>
              Paste one or more references in any format — APA, MLA, Chicago, numbered list.
            </p>
            <textarea value={raw} onChange={e => setRaw(e.target.value)} rows={8}
              placeholder={`Smith, J. (2020). The title of the article. Journal of Examples, 12(3), 45–67.\nhttps://doi.org/10.1234/example\n\nJones, M. & Brown, K. (2019). Another study. Academic Press.`}
              className={inputCls + ' resize-none'} style={{ ...inputSty, lineHeight: 1.6 }} {...focusHandlers} />
            {error && (
              <div className="flex items-center gap-2 mt-2 text-xs font-sans" style={{ color: '#DC2626' }}>
                <AlertCircle style={{ width: 12, height: 12 }} />{error}
              </div>
            )}
            <div className="flex gap-3 mt-4">
              <button onClick={onClose} className="flex-1 py-2.5 rounded-xl font-sans text-sm font-medium transition-all"
                style={{ border: `1px solid ${BORDER}`, color: MUTED }}
                onMouseEnter={e => e.currentTarget.style.background = BG}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                Cancel
              </button>
              <button onClick={parse} disabled={!raw.trim() || parsing}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-sans text-sm font-medium disabled:opacity-50 transition-all"
                style={{ background: GOLD, color: '#FFFFFF' }}>
                {parsing ? <Loader2 style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} /> : <Sparkles style={{ width: 13, height: 13 }} />}
                Parse with AI
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="font-sans text-xs mb-3" style={{ color: FAINT }}>
              {parsed.length} reference{parsed.length !== 1 ? 's' : ''} found. Review before importing.
            </p>
            <div className="space-y-2 max-h-72 overflow-y-auto mb-4 scrollbar-thin">
              {parsed.map((r, i) => (
                <div key={i} className="rounded-xl px-3.5 py-3" style={{ background: BG, border: `1px solid ${BORDER}` }}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-sans text-xs font-semibold" style={{ color: TEXT }}>{r.title}</p>
                    <TypeChip type={r.type} />
                  </div>
                  <p className="font-sans text-xs mt-1" style={{ color: MUTED }}>{r.authors} {r.year ? `(${r.year})` : ''}</p>
                  {r.apa_formatted && (
                    <p className="font-mono text-xs mt-1.5 italic leading-relaxed" style={{ color: FAINT, fontSize: 10 }}>{r.apa_formatted}</p>
                  )}
                </div>
              ))}
            </div>
            {error && <p className="font-sans text-xs mb-3" style={{ color: '#DC2626' }}>{error}</p>}
            <div className="flex gap-3">
              <button onClick={() => setParsed(null)}
                className="flex-1 py-2.5 rounded-xl font-sans text-sm font-medium transition-all"
                style={{ border: `1px solid ${BORDER}`, color: MUTED }}
                onMouseEnter={e => e.currentTarget.style.background = BG}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                Back
              </button>
              <button onClick={importAll} disabled={importing}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-sans text-sm font-medium disabled:opacity-50 transition-all"
                style={{ background: '#16A34A', color: '#FFFFFF' }}>
                {importing && <Loader2 style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} />}
                Import All ({parsed.length})
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}

// ─── Add / Edit modal ─────────────────────────────────────────────────────────

function AddModal({ existing, onSave, onClose }) {
  const [tab,    setTab]    = useState('manual')
  const [saving, setSaving] = useState(false)
  const [error,  setError]  = useState('')

  const save = async form => {
    setSaving(true)
    setError('')
    try {
      // The server rebuilds apa_formatted from these fields, so it is not sent.
      const record = {
        title:   form.title   || '',
        authors: form.authors || '',
        year:    form.year    || null,
        journal: form.journal || '',
        volume:  form.volume  || '',
        issue:   form.issue   || '',
        pages:   form.pages   || '',
        doi:     form.doi     || '',
        url:     form.url     || '',
        type:    form.type    || 'JOURNAL',
      }
      if (existing) {
        const { data } = await api.put(`/references/${existing.id}`, record)
        onSave(data, 'edit')
      } else {
        const { data } = await api.post('/references', record)
        onSave(data, 'add')
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not save this reference.')
      setSaving(false)
    }
  }

  return (
    <Modal onClose={onClose}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <p className="font-sans font-semibold text-sm" style={{ color: TEXT }}>
            {existing ? 'Edit Reference' : 'Add Reference'}
          </p>
          <button onClick={onClose} className="p-1.5 rounded-lg transition-colors"
            onMouseEnter={e => e.currentTarget.style.background = BG}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <X style={{ width: 15, height: 15, color: FAINT }} />
          </button>
        </div>

        {!existing && (
          <div className="flex gap-1 mb-5 p-1 rounded-xl" style={{ background: BG, border: `1px solid ${BORDER}` }}>
            {[{ id: 'manual', label: 'Manual Entry' }, { id: 'paste', label: 'Paste & Parse' }].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className="flex-1 py-2 rounded-lg font-sans text-xs font-medium transition-all"
                style={{
                  background: tab === t.id ? SURFACE : 'transparent',
                  color: tab === t.id ? TEXT : FAINT,
                  border: tab === t.id ? `1px solid ${BORDER}` : '1px solid transparent',
                  boxShadow: tab === t.id ? '0 1px 3px rgba(12,22,41,0.06)' : 'none',
                }}>
                {t.label}
              </button>
            ))}
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 mb-3 text-xs font-sans" style={{ color: '#DC2626' }}>
            <AlertCircle style={{ width: 12, height: 12, flexShrink: 0 }} />{error}
          </div>
        )}

        {tab === 'manual' || existing
          ? <ManualForm
              initial={existing ? {
                ...existing,
                year:    existing.year    || '',
                doi:     existing.doi     || '',
                url:     existing.url     || '',
                journal: existing.journal || '',
                volume:  existing.volume  || '',
                issue:   existing.issue   || '',
                pages:   existing.pages   || '',
              } : EMPTY_REF}
              onSave={save}
              onCancel={onClose}
              saving={saving}
            />
          : <PasteParseModal onImport={refs => onSave(refs, 'bulk')} onClose={onClose} />
        }
      </div>
    </Modal>
  )
}

// ─── Sort icon ────────────────────────────────────────────────────────────────

function SortIcon({ field, sortKey, dir }) {
  if (sortKey !== field) return <ChevronUp style={{ width: 11, height: 11, color: '#CBD8E8' }} />
  return dir === 'asc'
    ? <ChevronUp   style={{ width: 11, height: 11, color: GOLD }} />
    : <ChevronDown style={{ width: 11, height: 11, color: GOLD }} />
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ReferencesPage() {
  const [refs,      setRefs]      = useState([])
  const [loading,   setLoading]   = useState(true)
  const [view,      setView]      = useState('table')
  const [modal,     setModal]     = useState(null)
  const [sortKey,   setSortKey]   = useState('year')
  const [sortDir,   setSortDir]   = useState('desc')
  const [deleting,  setDeleting]  = useState(null)
  const [showPaste, setShowPaste] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [query,     setQuery]     = useState('')
  const [typeFilter, setTypeFilter] = useState('ALL')
  const [verifying, setVerifying] = useState(false)
  const [verifyMsg, setVerifyMsg] = useState('')

  const runVerify = async () => {
    if (!refs.length) return
    setVerifying(true); setVerifyMsg(''); setLoadError('')
    try {
      const { data } = await api.post('/references/verify', {})
      const s = data?.summary || {}
      setVerifyMsg(
        `Checked ${data?.checked || 0}: ${s.VERIFIED || 0} verified, ` +
        `${s.NOT_FOUND || 0} not found, ${s.NO_MATCH || 0} inconclusive.`
      )
      await load()
    } catch (err) {
      setLoadError(err?.response?.data?.error || 'Verification failed.')
    } finally { setVerifying(false) }
  }

  useEffect(() => { load() }, [])

  const load = async () => {
    try {
      const { data } = await api.get('/references')
      setRefs(Array.isArray(data) ? data : [])
      setLoadError('')
    } catch (err) {
      console.error('Load references error:', err)
      setLoadError(err?.response?.data?.error || err?.message || 'Could not load your reference library.')
    } finally { setLoading(false) }
  }

  const q = query.trim().toLowerCase()
  const filtered = refs.filter(r => {
    if (typeFilter !== 'ALL' && (r.type || 'OTHER') !== typeFilter) return false
    if (!q) return true
    return ['authors', 'title', 'journal', 'year', 'doi', 'apa_formatted']
      .some(k => String(r[k] || '').toLowerCase().includes(q))
  })

  const sorted = [...filtered].sort((a, b) => {
    let va = a[sortKey], vb = b[sortKey]
    if (sortKey === 'year') { va = va || 0; vb = vb || 0 }
    else { va = (va || '').toLowerCase(); vb = (vb || '').toLowerCase() }
    return sortDir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1)
  })

  const toggleSort = key => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(key); setSortDir('desc') }
  }

  const handleSave = (data, mode) => {
    if (mode === 'bulk') setRefs(prev => [...(Array.isArray(data) ? data : [data]), ...prev])
    else if (mode === 'edit') setRefs(prev => prev.map(r => r.id === data.id ? data : r))
    else setRefs(prev => [data, ...prev])
    setModal(null); setShowPaste(false)
  }

  const handleDelete = async id => {
    setDeleting(id)
    try {
      await api.delete(`/references/${id}`)
      setRefs(prev => prev.filter(r => r.id !== id))
    } catch (err) {
      console.error('Delete error:', err)
      setLoadError(err?.response?.data?.error || err?.message || 'Could not delete that reference.')
    }
    finally { setDeleting(null) }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 3.5rem)' }}>
        <Loader2 style={{ width: 22, height: 22, color: GOLD, animation: 'spin 1s linear infinite' }} />
      </div>
    )
  }

  return (
    <div className="max-w-6xl">

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="font-display text-xl font-semibold" style={{ color: TEXT }}>Reference Library</h1>
            <p className="font-sans text-xs mt-0.5" style={{ color: FAINT }}>
              Manage, import and AI-parse your academic references
            </p>
          </div>
          <span className="font-mono text-xs px-2.5 py-1 rounded-lg"
            style={{ background: GOLD_BG, color: '#1d4ed8', border: '1px solid rgba(37,99,235,0.2)' }}>
            {refs.length} refs
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex gap-0.5 p-1 rounded-lg" style={{ background: BG, border: `1px solid ${BORDER}` }}>
            <button onClick={() => setView('table')} className="p-1.5 rounded-md transition-all"
              style={{ background: view === 'table' ? SURFACE : 'transparent', color: view === 'table' ? TEXT : FAINT,
                       boxShadow: view === 'table' ? '0 1px 2px rgba(12,22,41,0.06)' : 'none' }}>
              <LayoutList style={{ width: 14, height: 14 }} />
            </button>
            <button onClick={() => setView('card')} className="p-1.5 rounded-md transition-all"
              style={{ background: view === 'card' ? SURFACE : 'transparent', color: view === 'card' ? TEXT : FAINT,
                       boxShadow: view === 'card' ? '0 1px 2px rgba(12,22,41,0.06)' : 'none' }}>
              <LayoutGrid style={{ width: 14, height: 14 }} />
            </button>
          </div>
          {refs.length > 0 && (
            <button onClick={runVerify} disabled={verifying}
              title="Check every reference against CrossRef"
              className="flex items-center gap-2 px-3.5 py-2 rounded-xl font-sans text-xs font-medium transition-all disabled:opacity-50"
              style={{ border: `1px solid ${BORDER}`, color: MUTED, background: SURFACE }}
              onMouseEnter={e => { if (!verifying) { e.currentTarget.style.background = BG; e.currentTarget.style.color = TEXT } }}
              onMouseLeave={e => { e.currentTarget.style.background = SURFACE; e.currentTarget.style.color = MUTED }}>
              {verifying
                ? <Loader2 style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} />
                : <ShieldCheck style={{ width: 13, height: 13 }} />}
              {verifying ? 'Verifying…' : 'Verify all'}
            </button>
          )}
          <button onClick={() => setShowPaste(true)}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl font-sans text-xs font-medium transition-all"
            style={{ border: `1px solid ${BORDER}`, color: MUTED, background: SURFACE }}
            onMouseEnter={e => { e.currentTarget.style.background = BG; e.currentTarget.style.color = TEXT }}
            onMouseLeave={e => { e.currentTarget.style.background = SURFACE; e.currentTarget.style.color = MUTED }}>
            <Sparkles style={{ width: 13, height: 13 }} />Paste &amp; Parse
          </button>
          <button onClick={() => setModal('add')}
            className="flex items-center gap-2 px-4 py-2 rounded-xl font-sans text-xs font-semibold transition-all"
            style={{ background: GOLD, color: '#FFFFFF' }}
            onMouseEnter={e => e.currentTarget.style.background = '#3b82f6'}
            onMouseLeave={e => e.currentTarget.style.background = GOLD}>
            <Plus style={{ width: 13, height: 13 }} />Add Reference
          </button>
        </div>
      </div>

      {loadError && (
        <div className="flex items-center gap-2 rounded-xl px-4 py-3 mb-4 font-sans text-xs"
          style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626' }}>
          <AlertCircle style={{ width: 13, height: 13, flexShrink: 0 }} />{loadError}
        </div>
      )}

      {verifyMsg && (
        <div className="flex items-center gap-2 rounded-xl px-4 py-3 mb-4 font-sans text-xs"
          style={{ background: GOLD_BG, border: '1px solid rgba(37,99,235,0.2)', color: '#1d4ed8' }}>
          <ShieldCheck style={{ width: 13, height: 13, flexShrink: 0 }} />{verifyMsg}
        </div>
      )}

      {/* Search + filter — a PhD bibliography runs to hundreds of entries */}
      {refs.length > 0 && (
        <div className="flex items-center gap-2 mb-4">
          <div className="relative flex-1">
            <Search style={{ width: 13, height: 13, color: FAINT, position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search authors, title, journal, DOI…"
              className={inputCls} style={{ ...inputSty, paddingLeft: 32 }} {...focusHandlers} />
          </div>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
            className="px-3 py-2.5 rounded-lg font-sans text-sm outline-none"
            style={{ ...inputSty, cursor: 'pointer' }}>
            {['ALL', 'JOURNAL', 'BOOK', 'REPORT', 'WEBSITE', 'OTHER'].map(t => (
              <option key={t} value={t}>{t === 'ALL' ? 'All types' : t}</option>
            ))}
          </select>
          {(q || typeFilter !== 'ALL') && (
            <span className="font-mono text-xs px-2.5 py-1 rounded-lg whitespace-nowrap"
              style={{ background: GOLD_BG, color: '#1d4ed8', border: '1px solid rgba(37,99,235,0.2)' }}>
              {sorted.length} match{sorted.length !== 1 ? 'es' : ''}
            </span>
          )}
        </div>
      )}

      {/* No search matches */}
      {refs.length > 0 && sorted.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 gap-2">
          <p className="font-sans text-sm font-medium" style={{ color: TEXT }}>No references match that search</p>
          <button onClick={() => { setQuery(''); setTypeFilter('ALL') }}
            className="font-sans text-xs underline" style={{ color: GOLD }}>Clear filters</button>
        </div>
      )}

      {/* Empty state */}
      {refs.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24 gap-4">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
            style={{ background: BG, border: `1px solid ${BORDER}` }}>
            <BookOpen style={{ width: 24, height: 24, color: FAINT }} />
          </div>
          <div className="text-center">
            <p className="font-sans text-sm font-semibold mb-1" style={{ color: TEXT }}>Your reference library is empty</p>
            <p className="font-sans text-xs" style={{ color: FAINT }}>Add references manually or paste and parse them with AI.</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setShowPaste(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-sans text-sm font-medium transition-all"
              style={{ border: `1px solid ${BORDER}`, color: MUTED, background: SURFACE }}
              onMouseEnter={e => e.currentTarget.style.background = BG}
              onMouseLeave={e => e.currentTarget.style.background = SURFACE}>
              <Sparkles style={{ width: 14, height: 14 }} />Paste &amp; Parse
            </button>
            <button onClick={() => setModal('add')}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-sans text-sm font-medium transition-all"
              style={{ background: GOLD, color: '#FFFFFF' }}
              onMouseEnter={e => e.currentTarget.style.background = '#3b82f6'}
              onMouseLeave={e => e.currentTarget.style.background = GOLD}>
              <Plus style={{ width: 14, height: 14 }} />Add Reference
            </button>
          </div>
        </div>
      )}

      {/* Table view */}
      {view === 'table' && sorted.length > 0 && (
        <div className="rounded-2xl overflow-hidden shadow-card" style={{ border: `1px solid ${BORDER}`, background: SURFACE }}>
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: `1px solid ${BORDER}`, background: BG }}>
                {[['#','',40],['Authors','authors',180],['Year','year',70],['Title','title',null],['Type','type',90]].map(([label, key, w]) => (
                  <th key={label}
                    onClick={() => key && toggleSort(key)}
                    className={`text-left px-4 py-3 font-sans ${key ? 'cursor-pointer' : ''}`}
                    style={{ width: w || undefined, userSelect: 'none', fontSize: 11, fontWeight: 500, color: FAINT, letterSpacing: '0.04em' }}>
                    <span className="flex items-center gap-1">
                      {label.toUpperCase()}{key && <SortIcon field={key} sortKey={sortKey} dir={sortDir} />}
                    </span>
                  </th>
                ))}
                <th className="px-4 py-3" style={{ width: 80, fontSize: 11, fontWeight: 500, color: FAINT, textAlign: 'left' }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((ref, i) => (
                <tr key={ref.id} style={{ borderBottom: `1px solid ${BORDER}`, background: i % 2 === 0 ? SURFACE : '#FAFBFE' }}>
                  <td className="px-4 py-3 font-mono text-xs" style={{ color: FAINT }}>{i + 1}</td>
                  <td className="px-4 py-3 font-sans text-xs" style={{ color: MUTED, maxWidth: 180 }}>
                    <span className="truncate block" style={{ maxWidth: 175 }}>{ref.authors}</span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs font-medium" style={{ color: MUTED }}>{ref.year || '—'}</td>
                  <td className="px-4 py-3">
                    <p className="font-sans text-xs font-medium" style={{ color: TEXT, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ref.title}</p>
                    {ref.apa_formatted && (
                      <p className="font-mono text-xs mt-0.5 italic" style={{ color: FAINT, fontSize: 10, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ref.apa_formatted}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <TypeChip type={ref.type} />
                      <VerifyChip status={ref.verification_status} note={ref.verification_note} />
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-0.5">
                      <button onClick={() => setModal(ref)}
                        className="p-1.5 rounded-lg transition-all" title="Edit"
                        style={{ color: FAINT }}
                        onMouseEnter={e => { e.currentTarget.style.background = BG; e.currentTarget.style.color = MUTED }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = FAINT }}>
                        <Pencil style={{ width: 12, height: 12 }} />
                      </button>
                      <button onClick={() => handleDelete(ref.id)} disabled={deleting === ref.id}
                        className="p-1.5 rounded-lg transition-all disabled:opacity-40" title="Delete"
                        style={{ color: FAINT }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#FEF2F2'; e.currentTarget.style.color = '#DC2626' }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = FAINT }}>
                        {deleting === ref.id
                          ? <Loader2 style={{ width: 12, height: 12, animation: 'spin 1s linear infinite' }} />
                          : <Trash2 style={{ width: 12, height: 12 }} />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Card view */}
      {view === 'card' && sorted.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          {sorted.map(ref => (
            <div key={ref.id} className="rounded-2xl p-5 space-y-3 group relative transition-all shadow-card"
              style={{ background: SURFACE, border: `1px solid ${BORDER}` }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(12,22,41,0.10)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(12,22,41,0.08)'}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-1.5">
                  <TypeChip type={ref.type} />
                  <VerifyChip status={ref.verification_status} note={ref.verification_note} />
                </div>
                <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => setModal(ref)} className="p-1 rounded-lg transition-all"
                    style={{ color: FAINT }}
                    onMouseEnter={e => { e.currentTarget.style.background = BG; e.currentTarget.style.color = MUTED }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = FAINT }}>
                    <Pencil style={{ width: 12, height: 12 }} />
                  </button>
                  <button onClick={() => handleDelete(ref.id)} className="p-1 rounded-lg transition-all"
                    style={{ color: FAINT }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#FEF2F2'; e.currentTarget.style.color = '#DC2626' }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = FAINT }}>
                    <Trash2 style={{ width: 12, height: 12 }} />
                  </button>
                </div>
              </div>
              <p className="font-sans text-sm font-semibold leading-snug line-clamp-2" style={{ color: TEXT }}>{ref.title}</p>
              <p className="font-sans text-xs" style={{ color: MUTED }}>{ref.authors}</p>
              {ref.year && <p className="font-mono text-xs" style={{ color: FAINT }}>{ref.year}</p>}
              {ref.apa_formatted && (
                <p className="font-mono text-xs italic leading-relaxed line-clamp-3" style={{ color: FAINT, fontSize: 10 }}>
                  {ref.apa_formatted}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {modal && (
        <AddModal existing={modal === 'add' ? null : modal} onSave={handleSave} onClose={() => setModal(null)} />
      )}

      {showPaste && (
        <PasteParseModal onImport={refs => { handleSave(refs, 'bulk'); setShowPaste(false) }} onClose={() => setShowPaste(false)} />
      )}
    </div>
  )
}
